import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { AuthService, ExternalLink, FileService, LicenseService, LinksService } from '@isophi/core-legacy';
import { IconService, ToastService } from '@isophi/core-ui';
import {
  AttendanceEndCodeEnum,
  AttendanceFulfillmentMethodEnum,
  AttendanceStartCodeEnum,
  ChildProfileDetail,
  ChildProfileDetailSocialCategoryDisadvantageEnum,
  EducationCourseChoiceEnum,
  InsuranceCompanyEnum,
  LevelOfTalentEnum,
} from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';

import { environment } from '../../../../../../../environments/environment';
import { RouterLinkFactory } from '../../../../../../core/router/router-link.factory';
import { HelperService } from '../../../../../../core/services/helper.service';
import {
  attendanceEndCodeData,
  attendanceFulfillmentMethodData,
  attendanceStartCodeData,
  educationCourseChoiceData,
  insuranceCompanyData,
  measureLevelData,
  socialCategoryDisadvantageData,
} from '../../../../components/child-form/data';

@Component({
  selector: 'app-child-info-education-tab',
  templateUrl: './child-info-education-tab.component.html',
  styleUrls: ['./child-info-education-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChildInfoEducationTabComponent implements OnInit {
  private readonly httpClient = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly fileService = inject(FileService);
  private readonly toastService = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly licenseService = inject(LicenseService);
  private readonly linksService = inject(LinksService);

  protected readonly transitionReportEnabled$ = this.licenseService.canUseTransitionReport;

  @Input()
  child: ChildProfileDetail;

  attendanceStartCode: { [key: string]: string }[] = [];

  attendanceFulfillmentMethod: { [key: string]: string }[] = [];

  attendanceEndCode: { [key: string]: string }[] = [];

  educationCourseChoice: { [key: string]: string }[] = [];

  insuranceCompany: { [key: string]: string }[] = [];

  socialCategoryDisadvantage = socialCategoryDisadvantageData;

  protected readonly socialCategoryDisadvantageEnum = ChildProfileDetailSocialCategoryDisadvantageEnum;

  measureLevel = measureLevelData;

  levelOfTalentEnum = LevelOfTalentEnum;

  protected tAppUrl: ExternalLink | null = null;

  constructor(protected iconsService: IconService, private helperService: HelperService, protected links: RouterLinkFactory) {}

  ngOnInit(): void {
    this.attendanceStartCode = this.helperService.prepareData(attendanceStartCodeData, AttendanceStartCodeEnum);
    this.attendanceFulfillmentMethod = this.helperService.prepareData(attendanceFulfillmentMethodData, AttendanceFulfillmentMethodEnum);
    this.attendanceEndCode = this.helperService.prepareData(attendanceEndCodeData, AttendanceEndCodeEnum);
    this.educationCourseChoice = this.helperService.prepareData(educationCourseChoiceData, EducationCourseChoiceEnum);
    this.insuranceCompany = this.helperService.prepareData(insuranceCompanyData, InsuranceCompanyEnum);

    this.tAppUrl = this.linksService.getTeacherAppUrl({
      redirect: `/transition-report/${this.child.childId}?external-redirect=true`,
    });
  }

  downloadTransitionReport() {
    this.spinner.show();
    const url = `${environment.pdfApiBase}/api/v1/transition-reports/basic/`;
    return this.httpClient
      .post(
        url,
        { child_uuid: this.child.childId },
        {
          responseType: 'blob',
          headers: new HttpHeaders({
            authorization: 'Bearer ' + this.authService.accessToken,
          }),
        }
      )
      .subscribe({
        next: (response) => {
          this.spinner.hide();
          this.fileService.downloadFile(
            response,
            `${this.translocoService.translate('reports/transition')}-r-${this.child.firstName}-${this.child.lastName}.pdf`
          );
        },
        error: () => {
          this.spinner.hide();
          this.toastService.error(this.translocoService.translate('transitionReport/downloadError'));
        },
      });
  }
}
